/**
 * Cicha Pewność - analityka zapisuje tylko intencję konwersji; identyfikatory reklamowe mogą działać wyłącznie po odpowiedniej zgodzie cookies.
 */
import type { CookiePreferences } from "./cookie-consent";

export type ConversionEvent =
  | "contact_phone_click"
  | "meeting_slot_selected"
  | "contact_form_submit"
  | "contract_template_request"
  | "form_submit"
  | "click_tel"
  | "test_complete"
  | "test_cta_click";

type GoogleWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  __upadlosciConsentDefaultApplied?: boolean;
};

const GTM_ID = "GTM-PC324P7M";
const GOOGLE_ADS_TAG_ID = "AW-331376299";
const MANUS_ANALYTICS_WEBSITE_ID = "096483f9-7cb9-44b6-9e71-fe07ac15a0a1";
const GTM_DEBUG_COOKIE = "upadlosci_gtm_debug=1";

function getGoogleWindow() {
  return window as GoogleWindow;
}

export function isGtmDebugSession() {
  if (typeof window === "undefined") return false;

  const hasGtmDebugParameter = new URLSearchParams(window.location.search).has(
    "gtm_debug"
  );
  const hasGtmDebugCookie = document.cookie
    .split(";")
    .some(cookie => cookie.trim() === GTM_DEBUG_COOKIE);

  try {
    if (hasGtmDebugParameter)
      window.sessionStorage.setItem("upadlosci-gtm-debug", "1");
    return (
      hasGtmDebugParameter ||
      hasGtmDebugCookie ||
      window.sessionStorage.getItem("upadlosci-gtm-debug") === "1"
    );
  } catch {
    return hasGtmDebugParameter || hasGtmDebugCookie;
  }
}

export function initializeGoogleConsentMode() {
  if (typeof window === "undefined") return;
  const current = getGoogleWindow();
  current.dataLayer ??= [];
  current.gtag ??= function gtag(..._args: unknown[]) {
    current.dataLayer?.push(arguments);
  };
  if (current.__upadlosciConsentDefaultApplied) return;
  current.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  current.__upadlosciConsentDefaultApplied = true;
}

function activateGtm() {
  if (
    typeof document === "undefined" ||
    document.querySelector("script[data-upadlosci-gtm]")
  )
    return;
  const current = getGoogleWindow();
  current.dataLayer ??= [];
  current.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  script.dataset.upadlosciGtm = "true";
  document.head.appendChild(script);
}

export function activateGtmForDebug() {
  if (isGtmDebugSession()) activateGtm();
}

function activateGoogleAdsTag() {
  if (
    typeof document === "undefined" ||
    document.querySelector("script[data-upadlosci-google-ads]")
  )
    return;

  const current = getGoogleWindow();
  current.dataLayer ??= [];
  current.gtag ??= function gtag(..._args: unknown[]) {
    current.dataLayer?.push(arguments);
  };
  current.gtag("js", new Date());
  current.gtag("config", GOOGLE_ADS_TAG_ID);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_TAG_ID}`;
  script.dataset.upadlosciGoogleAds = "true";
  document.head.appendChild(script);
}

function deactivateGoogleAdsTag() {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll("script[data-upadlosci-google-ads]")
    .forEach(script => script.remove());
}

function activateManusAnalytics() {
  if (
    typeof document === "undefined" ||
    document.querySelector("script[data-upadlosci-manus-analytics]")
  )
    return;

  window.localStorage.removeItem("umami.disabled");
  const script = document.createElement("script");
  script.async = true;
  script.src = "/analytics/manus.js";
  script.dataset.upadlosciManusAnalytics = "true";
  script.dataset.websiteId = MANUS_ANALYTICS_WEBSITE_ID;
  script.dataset.excludeSearch = "true";
  script.dataset.excludeHash = "true";
  document.head.appendChild(script);
}

function deactivateManusAnalytics() {
  if (typeof document === "undefined") return;
  window.localStorage.setItem("umami.disabled", "1");
  document
    .querySelectorAll("script[data-upadlosci-manus-analytics]")
    .forEach(script => script.remove());
}

function applyManusAnalyticsPreference(enabled: boolean) {
  if (enabled) activateManusAnalytics();
  else deactivateManusAnalytics();
}

function deactivateOptionalScripts(category: "marketing") {
  if (isGtmDebugSession()) return;
  const selector = "script[data-upadlosci-gtm]";
  document.querySelectorAll(selector).forEach(script => script.remove());
}

export function applyCookiePreferences(preferences: CookiePreferences) {
  if (typeof window === "undefined") return;
  initializeGoogleConsentMode();
  const current = getGoogleWindow();
  current.gtag?.("consent", "update", {
    analytics_storage: preferences.analytics ? "granted" : "denied",
    ad_storage: preferences.marketing ? "granted" : "denied",
    ad_user_data: preferences.marketing ? "granted" : "denied",
    ad_personalization: preferences.marketing ? "granted" : "denied",
  });
  applyManusAnalyticsPreference(preferences.analytics);
  if (preferences.analytics || preferences.marketing) activateGtm();
  if (preferences.marketing) activateGoogleAdsTag();
  else deactivateGoogleAdsTag();
  if (!preferences.analytics && !preferences.marketing)
    deactivateOptionalScripts("marketing");
}

export function trackConversion(
  event: ConversionEvent,
  params: Record<string, string | number | boolean> = {}
) {
  if (typeof window === "undefined") return;
  const gtmEvent =
    event === "contact_phone_click" || event === "click_tel"
      ? "click_to_call"
      : [
            "meeting_slot_selected",
            "contact_form_submit",
            "form_submit",
          ].includes(event)
        ? "generate_lead"
        : event === "test_complete"
          ? "quiz_complete"
          : event;
  window.dispatchEvent(
    new CustomEvent("upadlosci:conversion", {
      detail: { event: gtmEvent, source_event: event, ...params },
    })
  );
  getGoogleWindow().dataLayer?.push({
    event: gtmEvent,
    source_event: event,
    ...params,
  });
}
